import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { agents, statusMeta, toneSurface } from "@/lib/mockData";
import { cn } from "@/lib/utils";

/**
 * Left rail — server-driven previews per agent.
 *
 * Props:
 *  - previews: { [agent_id]: { last_message_preview, updated_at, unread_for_user } }
 *  - activeId
 *  - onSelect(agent_id)
 */
export default function AgentList({ previews, activeId, onSelect }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.role.toLowerCase().includes(q) ||
        a.focus.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <aside
      data-testid="agent-list"
      className="flex h-full w-full flex-shrink-0 flex-col border-r border-black/[0.04] bg-[#FBFBFA] md:w-[300px]"
    >
      <div className="px-4 pb-3 pt-5">
        <div className="eyebrow mb-3 px-1">Agents</div>
        <div className="flex items-center gap-2 rounded-full border border-black/[0.04] bg-white px-3.5 py-2 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
          <Search className="h-3.5 w-3.5 text-zinc-400" strokeWidth={2.4} />
          <input
            data-testid="agent-list-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search agents..."
            className="w-full bg-transparent text-[12.5px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none"
          />
        </div>
      </div>

      <nav className="idea-scroll flex-1 space-y-1 overflow-y-auto px-3 pb-4">
        {filtered.map((agent) => {
          const preview = previews?.[agent.id] ?? {};
          const isActive = activeId === agent.id;
          const hasUnread = !!preview.unread_for_user;
          const status = statusMeta[agent.status] ?? statusMeta.idle;
          const tone = toneSurface[agent.tone] ?? toneSurface.neutral;
          const isDarkAvatar = agent.tone === "dark";
          const previewText = preview.last_message_preview || "No messages yet.";

          return (
            <button
              key={agent.id}
              type="button"
              data-testid={`agent-list-row-${agent.id}`}
              onClick={() => onSelect(agent.id)}
              className={cn(
                "group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors",
                isActive
                  ? "bg-black text-white"
                  : "hover:bg-black/[0.04] text-zinc-900",
              )}
            >
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full text-[14px] font-extrabold",
                    isDarkAvatar
                      ? "bg-[#0B0B0E] text-white"
                      : cn(tone.bg, "text-zinc-900"),
                  )}
                  style={{
                    boxShadow:
                      "inset 0 0 0 1px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)",
                  }}
                >
                  {agent.name[0]}
                </div>
                <span
                  className={cn(
                    "absolute -bottom-0.5 -right-0.5 inline-block h-2.5 w-2.5 rounded-full ring-2",
                    isActive ? "ring-black" : "ring-[#FBFBFA]",
                  )}
                  style={{ backgroundColor: status.dot }}
                />
              </div>

              {/* Text */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div
                    className={cn(
                      "truncate text-[13.5px] font-bold",
                      isActive ? "text-white" : "text-zinc-900",
                    )}
                  >
                    {agent.name}
                  </div>
                  <div
                    className={cn(
                      "flex-shrink-0 text-[10px] font-bold uppercase tracking-[0.14em]",
                      isActive ? "text-white/70" : "text-zinc-400",
                    )}
                  >
                    {agent.role}
                  </div>
                </div>
                <div
                  className={cn(
                    "mt-0.5 flex items-center gap-1.5 truncate text-[12px]",
                    isActive ? "text-white/65" : "text-zinc-500",
                  )}
                >
                  <span
                    data-testid={`agent-list-preview-${agent.id}`}
                    className="truncate"
                  >
                    {previewText}
                  </span>
                </div>
              </div>

              {/* Unread dot */}
              {hasUnread && !isActive && (
                <span
                  data-testid={`agent-list-unread-${agent.id}`}
                  className="ml-1 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-violet-500"
                />
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-3 py-8 text-center text-[12px] text-zinc-400">
            No agents match your search.
          </div>
        )}
      </nav>
    </aside>
  );
}
